import { useState, type Dispatch, type SetStateAction } from "react";
import { StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import { useLocale } from "../primitives/useLocale";
import type { UnitsPreference } from "@heraion/shared";
import type { Exercise, Plan, Program, ProgramModel, SelfUnassign } from "@heraion/shared";

import type { UiTheme } from "../theme";
import type { ProgramApiFetch } from "../ProgramEditor/types";
import type { DurableWriteFn } from "../seams";
import { useBreakpoint } from "../useBreakpoint";
import { useExerciseNameResolver } from "../Progress/useExerciseNameResolver";
import { AssignSheet } from "../ProgramComposer/AssignSheet";
import { LibraryItemCard } from "./LibraryItemCard";
import { useAssignedByCoachNames } from "./useAssignedByCoachNames";
import { buildLibraryItemCardProps } from "./buildLibraryItemCardProps";
import type { LibraryTagFilters } from "./libraryFilters";
import {
  libraryItemName,
  useLibrary,
  type LibraryFilter,
  type LibraryItem,
} from "./useLibrary";
import { useLibraryActions } from "./useLibraryActions";
import { useLibraryAssignSheet } from "./useLibraryAssignSheet";
import { useLibraryBrowse } from "./useLibraryBrowse";
import { LibraryResults } from "./LibraryResults";
import { SaveAsModelDialog } from "./SaveAsModelDialog";

// The browsable library inside the Create-hub (redesign track HR §S3):
// filter-tabs (chips acting as single-select tabs) over the composed
// program/plan buckets, grouped by source, one LibraryItemCard per row.
// Narrow = the stacked phone column; wide = a two-column card grid inside
// the hub dialog (useBreakpoint — no Platform.OS).
//
// CO-23 split: this file is now the HOST — data (useLibrary), the guarded
// management writes (useLibraryActions), AssignSheet, and the per-row card.
// *What to show* moved out and is shared with the coach's program picker:
// derivation (tab + tag state, sections, empty/loading/error) in
// useLibraryBrowse.ts, rendering in LibraryResults.tsx, which takes the row
// as an injected `renderItem` and so knows nothing about LibraryItemCard.
// The filter-tab list (FILTERS/filtersForView) stays in libraryFilters.ts.

export type LibraryBrowserProps = {
  theme: UiTheme;
  dark: boolean;
  apiFetch: ProgramApiFetch;
  /** Host write seam — mobile queues when offline, web sends only. */
  durableWrite: DurableWriteFn;
  /** Live catalog for the inline previews (muscles + exercise names).
   *  null while the host is still loading it — previews degrade to slugs. */
  catalog: Exercise[] | null;
  /** Display unit for prescribed loads in the inline plan previews.
   *  Loads are stored in kg; an lb reader must see lb here too. Required. */
  unit: UnitsPreference;
  /** Personal-row management (HR-S4 — the Library owns it now that the
   *  Home lists retired): open the composer on an item. Capability-gated
   *  by omission, like every optional handler here. */
  onEditProgram?: (program: Program) => void;
  onEditPlan?: (plan: Plan) => void;
  /** After «Save as model»: plan a program from that model. Absent: no such action. */
  onPlanFromModel?: (model: Omit<ProgramModel, "id">) => void;
  /** Personal plans on hosts with a logger (mobile): start the session. */
  onStartPlan?: (plan: Plan) => void;
  /** Id of the plan whose start is resolving (double-tap guard). */
  startingPlanId?: string | null;
  /** Host delete-confirm seam: when provided, the host confirms (mobile
   *  Alert) and calls `run` on OK; omitted → two-tap inline on the card.
   *  CO-34: `leaving` is non-null when the press takes the caller off
   *  `assignedTo` instead of destroying the row, so the host prompt can
   *  say what actually happens (and to how many other people). A host that
   *  ignores it keeps a two-parameter signature and still type-checks. */
  confirmDelete?: (
    item: LibraryItem,
    run: () => void,
    leaving?: SelfUnassign | null,
    /** True when the row is FOLLOWED: the press removes the caller's own
     *  pointer and the author keeps the program, so the prompt must not say
     *  Delete. Fourth optional parameter, same degrade as `leaving`. */
    unfollowing?: boolean,
  ) => void;
  /** The signed-in caller's sub: the "coach" tab discriminant, the view
   *  gate (athlete-view rows must be assigned to it) and the assigned-by
   *  badge. `null` means "not yet known" and matches nothing; see
   *  buildLibraryItemCardProps.ts and filterLibraryItems. */
  callerSub: string | null;
  /** CO-19 — the active view (Experience) this mount shows the library
   *  for, threaded straight through to useLibrary/filterLibraryItems.
   *  Optional and defaulting to `null` ("not yet known" ⇒ no filtering)
   *  so a host that hasn't been taught about the coach view yet (CO-18)
   *  keeps compiling and behaving exactly as before. */
  authoredAs?: Program["authoredAs"] | null;
  /** CO-21 — active-athlete subs for the standalone AssignSheet, same
   *  INJECTED-never-fetched contract as ComposerAssigneesBand's own
   *  athleteRoster (CN-coach-edges-fetch). Absent ⇒ `[]`. */
  athleteRoster?: ReadonlyArray<string>;
  /** MX-8b: optional controlled tab + tag filters, straight through to
   *  useLibraryBrowse (see its header). Omit for today's uncontrolled
   *  internal state (every caller but the Create-hub's own mounts). */
  filter?: LibraryFilter;
  onFilterChange?: (f: LibraryFilter) => void;
  tagFilters?: LibraryTagFilters;
  onTagFiltersChange?: Dispatch<SetStateAction<LibraryTagFilters>>;
};

export function LibraryBrowser({
  theme,
  dark,
  apiFetch,
  durableWrite,
  catalog,
  unit,
  onEditProgram,
  onEditPlan,
  onPlanFromModel,
  onStartPlan,
  startingPlanId,
  confirmDelete,
  callerSub,
  authoredAs = null,
  athleteRoster = [],
  filter: controlledFilter,
  onFilterChange,
  tagFilters: controlledTagFilters,
  onTagFiltersChange,
}: LibraryBrowserProps) {
  const { t } = useTranslation();
  const lang = useLocale();
  const styles = makeStyles(theme);
  const { wide } = useBreakpoint();
  const resolveName = useExerciseNameResolver(catalog);
  const lib = useLibrary({ apiFetch, durableWrite, callerSub, authoredAs });
  // PE-48 review fix #7 — ONE batch for every "assigned by <coach>" badge
  // on screen, instead of each LibraryItemCard resolving its own coach's
  // name (see useAssignedByCoachNames.ts for the duplicate-read this
  // replaces).
  const coachNames = useAssignedByCoachNames(apiFetch, lib.items, callerSub);
  // Guarded management writes (follow/publish/unfollow/delete) + their
  // busy keys and error surface — extracted so this stays layout+grouping.
  const {
    actionError,
    setActionError,
    followingKey,
    mutatingKey,
    onFollow,
    onSetStatus,
    requestDelete,
  } = useLibraryActions(lib, confirmDelete);

  // CO-21 — AssignSheet's draft state + save, split out like useLibraryActions.
  const {
    assigningItem,
    assignedToDraft,
    assigneeEmailsDraft,
    setAssignedToDraft,
    setAssigneeEmailsDraft,
    assignBusy,
    assignError,
    assignFlash,
    onAssign,
    closeAssignSheet,
    onSaveAssignees,
  } = useLibraryAssignSheet(lib, callerSub, t);
  // #354: the program «Save as model» is open on.
  const [modelItem, setModelItem] = useState<LibraryItem | null>(null);

  // Filter-tab state, tag-filter state and the section/loading/error
  // derivation — split out (CO-21, 500-line cap) so this file stays
  // layout+hosting only. See useLibraryBrowse.ts.
  const browse = useLibraryBrowse({
    lib,
    callerSub,
    catalog,
    filter: controlledFilter,
    onFilterChange,
    tagFilters: controlledTagFilters,
    onTagFiltersChange,
  });

  const onPickFilter = (f: LibraryFilter) => {
    // Stale action-error banner never outlives its tab — actionError is
    // owned by useLibraryActions (a hosting concern), so it's cleared here
    // alongside the hook's own tab-switch reset (feedback + tag filters).
    setActionError(null);
    browse.onPickFilter(f);
  };

  const onStar = async (item: LibraryItem) => {
    if (item.source !== "personal") return;
    const itemId = item.kind === "program" ? item.program.id : item.plan.id;
    const err = await lib.toggleFavorite(item.kind, itemId);
    if (err) {
      // Raw code (api_error_NNN) is a breadcrumb, not UI copy.
      console.warn("[library] favorite_failed", err.message);
      setActionError(
        t("library.errFavorite", {
          defaultValue: "Could not update favorites.",
        }),
      );
    }
  };

  const renderItem = (item: LibraryItem) => (
    <LibraryItemCard
      {...buildLibraryItemCardProps({
        item,
        theme,
        dark,
        catalog,
        resolveName,
        unit,
        apiFetch,
        callerSub,
        coachNames,
        authoredAs: lib.authoredAs,
        lib,
        followingKey,
        mutatingKey,
        startingPlanId,
        onFollow,
        onStar,
        onSetStatus,
        onStartPlan,
        onEditProgram,
        onEditPlan,
        onSaveAsModel: setModelItem,
        onAssign,
        requestDelete,
        confirmDelete,
      })}
    />
  );

  return (
    <View style={styles.wrap}>
      <LibraryResults
        theme={theme}
        wide={wide}
        authoredAs={lib.authoredAs}
        // `onPickFilter` overridden: the host also clears the action-error
        // banner on tab switch (below), so this must be the wrapped version,
        // not the hook's own raw one — everything else rides through as-is.
        browse={{ ...browse, onPickFilter }}
        banners={{
          cloneFlash: lib.cloneFlash,
          cloneError: lib.cloneError,
          actionError,
          assignFlash,
        }}
        renderItem={renderItem}
      />

      {assigningItem ? (
        <AssignSheet
          theme={theme}
          visible
          title={libraryItemName(assigningItem, lang)}
          apiFetch={apiFetch}
          kind={assigningItem.kind === "program" ? "program" : "plans"}
          authoredAs={authoredAs ?? undefined}
          callerSub={callerSub}
          athleteRoster={athleteRoster}
          assignedTo={assignedToDraft}
          assigneeEmails={assigneeEmailsDraft}
          onChangeAssignedTo={setAssignedToDraft}
          onChangeAssigneeEmails={setAssigneeEmailsDraft}
          busy={assignBusy}
          error={assignError}
          onCancel={closeAssignSheet}
          onSave={() => void onSaveAssignees()}
        />
      ) : null}

      {modelItem?.source === "personal" && modelItem.kind === "program" ? (
        <SaveAsModelDialog
          theme={theme}
          program={modelItem.program}
          defaultName={libraryItemName(modelItem, lang)}
          catalog={catalog}
          unit={unit}
          apiFetch={apiFetch}
          durableWrite={durableWrite}
          resolveName={resolveName}
          onPlanFromModel={onPlanFromModel}
          onClose={() => setModelItem(null)}
        />
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { spacing: s } = theme;
  return StyleSheet.create({
    wrap: {
      gap: s.s3,
    },
  });
}
